import { useEffect, useRef, useState } from "react";
import { nameOf } from "../../../../utils/functions/ReflectionUtil";
import { PrimaryButton } from "../../../components/Buttons/PrimaryButton";
import { ImageLogoForm } from "../models/ImageLogoForm";
import { InputWithSuffix } from "./InputWithSuffix";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsRotate } from "@fortawesome/free-solid-svg-icons";
import { TransferMethod } from "../../../../core/models/entities/LogoOptions";
import { correctLogoSizeForTransferMethod } from "../../../../utils/functions/SizeUtil";

interface Props {
  width: number;
  height: number;
  onSizeChange: (width: number, height: number) => void;
  onResize: () => void;
  disabled?: boolean;
  transferMethod?: TransferMethod | null;
}

export function LogoResize({
  width,
  height,
  onSizeChange,
  onResize,
  disabled = false,
  transferMethod,
}: Props) {
  const [inputWidth, setInputWidth] = useState(width.toString());
  const [inputHeight, setInputHeight] = useState(height.toString());

  const prevWidthRef = useRef(width);
  const prevHeightRef = useRef(height);

  useEffect(() => {
    if (prevWidthRef.current !== width) {
      setInputWidth(width.toString());
      prevWidthRef.current = width;
    }
  }, [width]);

  useEffect(() => {
    if (prevHeightRef.current !== height) {
      setInputHeight(height.toString());
      prevHeightRef.current = height;
    }
  }, [height]);

  function handleWidthChange(event: React.ChangeEvent<HTMLInputElement>) {
    const value = event.target.value;
    setInputWidth(value);

    if (
      value.trim().length === 0 ||
      parseFloat(value) === 0 ||
      isNaN(parseFloat(value))
    ) {
      return;
    }

    const newWidth = parseFloat(value);
    const newHeight = (newWidth / width) * height;

    // Apply size correction if transfer method is available
    if (transferMethod?.size) {
      const correctedSize = correctLogoSizeForTransferMethod(
        newWidth,
        newHeight,
        transferMethod.size,
      );
      onSizeChange(
        Math.round(correctedSize.width),
        Math.round(correctedSize.height),
      );
    } else {
      onSizeChange(Math.round(newWidth), Math.round(newHeight));
    }
  }

  function handleHeightChange(event: React.ChangeEvent<HTMLInputElement>) {
    const value = event.target.value;
    setInputHeight(value);

    if (
      value.trim().length === 0 ||
      parseFloat(value) === 0 ||
      isNaN(parseFloat(value))
    ) {
      return;
    }

    const newHeight = parseFloat(value);
    const newWidth = (newHeight / height) * width;

    // Apply size correction if transfer method is available
    if (transferMethod?.size) {
      const correctedSize = correctLogoSizeForTransferMethod(
        newWidth,
        newHeight,
        transferMethod.size,
      );
      onSizeChange(
        Math.round(correctedSize.width),
        Math.round(correctedSize.height),
      );
    } else {
      onSizeChange(Math.round(newWidth), Math.round(newHeight));
    }
  }

  const isValid =
    !isNaN(parseFloat(inputWidth)) &&
    parseFloat(inputWidth) > 0 &&
    !isNaN(parseFloat(inputHeight)) &&
    parseFloat(inputHeight) > 0;

  return (
    <div className={"flex items-center space-x-2"}>
      <label className={"uppercase"}>Size</label>
      <InputWithSuffix
        disabled={disabled}
        className={"!w-20 h-9"}
        suffix={"MM"}
        name={nameOf<ImageLogoForm>((lf) => lf.widthMm)}
        type="number"
        value={inputWidth}
        onChange={handleWidthChange}
      />
      <label>X</label>
      <InputWithSuffix
        disabled={disabled}
        className={"!w-20 h-9"}
        suffix={"MM"}
        name={nameOf<ImageLogoForm>((lf) => lf.heightMm)}
        type="number"
        value={inputHeight}
        onChange={handleHeightChange}
      />
      <PrimaryButton
        width={"sm"}
        className={"px-4"}
        onClick={onResize}
        disabled={disabled || !isValid}
      >
        <FontAwesomeIcon icon={faArrowsRotate} />
        <span className={"flex-1"}>Change Size</span>
      </PrimaryButton>
    </div>
  );
}
